import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// ── The file manager's action menus ───────────────────────────────────────────────────────────────────────────────
// ONE menu component, two sets of rows:
//   itemMenuRows()      - the ⋮ on a card (and right-click / long-press): every action for that ONE file or folder
//   selectionMenuRows() - the ⋮ in the selection bar on a phone: the actions that do not fit beside the icons there
// A row is plain data - { key, Icon, labelKey, danger } - so which action is offered to whom is read in one place. The
// permission keys are the server's own: renaming, moving, copying, pinning and tagging all need files:upload (those routes
// are guarded by it), deleting files:delete, sharing files:share. The server stays the real gate; a row the person may not
// use is simply not drawn.

const row = (key, Icon, labelKey, extra) => ({ key, Icon, labelKey, ...extra });

// Join groups of rows with dividers, skipping empty groups (and rows that are null).
function joinGroups(groups) {
  const rows = [];
  groups.map((g) => g.filter(Boolean)).filter((g) => g.length).forEach((g, i) => {
    if (i) rows.push('divider');
    rows.push(...g);
  });
  return rows;
}

/** Rows for ONE file / folder. canPasteInto: the clipboard holds something a folder could take. */
export function itemMenuRows({ can, isFolder, previewable, pinned, selected, canPasteInto }) {
  const edit = can('files:upload');
  return joinGroups([
    [
      isFolder ? row('open', FolderOpenIcon, 'files.openAction') : (previewable ? row('preview', VisibilityOutlinedIcon, 'common.preview') : null),
      row('details', InfoOutlinedIcon, 'files.detailsAction'),
    ],
    edit ? [
      row('rename', DriveFileRenameOutlineIcon, 'files.renameHeader'),
      row('cut', ContentCutIcon, 'files.cutTip'),
      row('copy', ContentCopyIcon, 'common.copy'),
      isFolder && canPasteInto ? row('pasteInto', ContentPasteIcon, 'files.pasteInto') : null,
      row('moveTo', DriveFileMoveIcon, 'files.moveTip'),
    ] : [],
    [
      row('download', DownloadIcon, 'common.download'),
      can('files:share') ? row('share', ShareIcon, 'common.share') : null,
      edit ? (pinned ? row('pin', StarIcon, 'files.unpinTip') : row('pin', StarBorderIcon, 'files.pinTip')) : null,
      row('select', selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon, selected ? 'files.deselectAction' : 'files.selectAction'),
    ],
    can('files:delete') ? [row('delete', DeleteOutlineIcon, 'common.delete', { danger: true })] : [],
  ]);
}

/** Rows for the SELECTION (any number of items), for the phone's selection bar. */
export function selectionMenuRows({ can }) {
  const edit = can('files:upload');
  return joinGroups([
    [row('download', DownloadIcon, 'common.download')],
    edit ? [
      row('moveTo', DriveFileMoveIcon, 'files.moveTip'),
      row('copyTo', FileCopyOutlinedIcon, 'files.copyToTip'),
      row('tag', LocalOfferIcon, 'files.tagTip'),
    ] : [],
    [
      can('files:share') ? row('share', ShareIcon, 'common.share') : null,
      row('selectAll', SelectAllIcon, 'files.selectAllAction'),
    ],
  ]);
}

/**
 * @param anchor   null (closed) | { anchorEl } (under a button) | { position: { top, left } } (where the pointer was)
 * @param rows     itemMenuRows() / selectionMenuRows()
 * @param onAction called with the row's key AFTER the menu has closed
 */
export default function FileItemMenu({ anchor, rows, onClose, onAction, label }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const rtl = theme.direction === 'rtl';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const open = Boolean(anchor) && rows.length > 0;
  const byPosition = Boolean(anchor && anchor.position);

  return (
    <Menu
      open={open} onClose={onClose}
      // Above the app's install / notification prompts (bottom sheets at z-index 1600): on a phone one of them sits over the bottom of
      // the screen until it is dismissed, and the lower rows of this (tall) menu - Select, Delete - ended up under it, untappable.
      sx={{ zIndex: (th) => th.zIndex.modal + 400 }}
      {...(byPosition
        ? { anchorReference: 'anchorPosition', anchorPosition: anchor.position }
        : {
          anchorEl: anchor ? anchor.anchorEl : null,
          anchorOrigin: { vertical: 'bottom', horizontal: rtl ? 'left' : 'right' },
          transformOrigin: { vertical: 'top', horizontal: rtl ? 'left' : 'right' },
        })}
      PaperProps={{
        'data-file-menu': '',
        sx: {
          minWidth: 210, borderRadius: '12px', backgroundImage: 'none',
          bgcolor: isDark ? '#181818' : 'background.paper',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : theme.palette.divider}`,
        },
      }}
      MenuListProps={{ dense: !isXs, 'aria-label': label }}
    >
      {rows.map((r, i) => (r === 'divider'
        ? <Divider key={`d${i}`} sx={{ my: 0.5 }} />
        : (
          <MenuItem key={r.key} data-menu-action={r.key}
            onClick={() => { onClose(); onAction(r.key); }}
            sx={{ minHeight: isXs ? 46 : 34, color: r.danger ? '#EA005A' : undefined }}>
            <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>
              <r.Icon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary={t(r.labelKey)} primaryTypographyProps={{ fontSize: '0.84rem' }} />
          </MenuItem>
        )))}
    </Menu>
  );
}
